/**
 * Pure PDF variant selection for the WBR viewer (pdf-margin-callouts).
 *
 * A single total function decides which of the three PDF variants the viewer
 * displays from the two toggle states and the per-report availability flags
 * (Req 4.1-4.6). Every URL choice in `WBRCalloutsViewer` — the active PDF and
 * merged-PDF memos, head reload, appended-report loading in continuous
 * scroll, and prefetch — routes through this one decision so the precedence
 * is a single source of truth that is directly unit testable (mirrors the
 * `canEditSummary.ts` extraction pattern).
 *
 * Precedence: margin wins while it is on and available, regardless of the
 * cell-refs toggle (the margin variant already contains the cell-reference
 * highlights); otherwise the highlighted variant wins while cell-refs is on
 * and it is available; otherwise the source PDF. Unavailability of a
 * requested variant falls through to the next choice rather than erroring
 * (Req 4.4, 4.5, 4.6).
 */

/** The three PDF variants a report can serve, in display precedence order. */
export type PdfVariantKind = "margin" | "highlighted" | "source";

/**
 * Availability flags for the two generated variants of the report being
 * displayed. The Source_PDF needs no flag: it is the unconditional fallback.
 */
export interface VariantAvailability {
  /** True when the report's Margin_Callout_PDF exists in storage. */
  margin: boolean;
  /** True when the report's Highlighted_PDF exists in storage. */
  highlighted: boolean;
}

/**
 * The PDF variant to display for the given toggle states and availability.
 *
 * Total over all 16 combinations of the four booleans: exactly one branch
 * applies to every input, so the caller always receives a variant and never
 * an error state.
 *
 * @param marginOn Current Margin_Toggle state.
 * @param cellRefsOn Current Cell_Refs_Toggle state.
 * @param avail Availability of the generated variants for the displayed
 *   report.
 * @returns The variant kind selected by the Req 4 precedence rules.
 */
export function selectPdfVariant(
  marginOn: boolean,
  cellRefsOn: boolean,
  avail: VariantAvailability
): PdfVariantKind {
  // Margin on and available: display the Margin_Callout_PDF regardless of
  // the Cell_Refs_Toggle state (Req 4.1).
  if (marginOn && avail.margin) return "margin";
  // Otherwise the Highlighted_PDF while cell-refs is on and it is available
  // (Req 4.2), including when margin is on but its variant is unavailable
  // (Req 4.4).
  if (cellRefsOn && avail.highlighted) return "highlighted";
  // Otherwise the Source_PDF: both toggles off (Req 4.3), margin on but
  // unavailable with no highlighted fallback (Req 4.5), or cell-refs on with
  // the Highlighted_PDF unavailable (Req 4.6).
  return "source";
}
